import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useAuth } from '@/contexts/AuthContext'
import { useInfiniteContactsQuery } from '@/hooks/useInfiniteContactsQuery'
import { usePullToRefresh } from '@/hooks/usePullToRefresh'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { Contact, ContactLabel } from '@/types/account'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Phone, Plus, Search, Import, Star, RefreshCw, ChevronRight, PhoneCall } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import ContactForm from './ContactForm'
import AuthOverlay from './AuthOverlay'
import PullToRefreshIndicator from './PullToRefreshIndicator'
import { useSyncGoogleContacts } from '@/hooks/useGoogleContacts'
import { accountApiService, accountQueries } from '@/services/accountApi'
import { AvatarPlaceholder } from '@/components/ui/avatar-placeholder'
import { PhoneNumber } from '@/components/ui/phone-number'
import { ContactDetailState } from './ContactDetailPanel'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HEADER_HEIGHT = 28
const ROW_HEIGHT = 60

// ---------------------------------------------------------------------------
// Flat virtual row types — processed in API arrival order (append-only)
// ---------------------------------------------------------------------------

type VirtualRow =
  | { type: 'header'; letter: string }
  | { type: 'contact'; contact: Contact }
  | { type: 'skeleton'; id: number }

function buildRows(contacts: Contact[]): VirtualRow[] {
  const rows: VirtualRow[] = []
  let currentLetter = ''

  for (const c of contacts) {
    const sortName = [c.lastName, c.firstName].filter(Boolean).join(' ') ||
      [c.firstName, c.lastName].filter(Boolean).join(' ')
    const firstChar = sortName.trim()[0]?.toUpperCase() ?? '#'
    const letter = /^[A-Z]$/.test(firstChar) ? firstChar : '#'

    if (letter !== currentLetter) {
      rows.push({ type: 'header', letter })
      currentLetter = letter
    }
    rows.push({ type: 'contact', contact: c })
  }

  return rows
}

// ---------------------------------------------------------------------------
// Types / props
// ---------------------------------------------------------------------------

type ContactTab = 'all' | ContactLabel

interface ContactsPanelProps {
  onOpenContact?: (detail: ContactDetailState) => void
  onCall?: (number: string) => void
  className?: string
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ContactsPanel: React.FC<ContactsPanelProps> = ({ onOpenContact, onCall, className }) => {
  const { t } = useTranslation(['app', 'common'])
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const isDesktop = useBreakpoint('lg')

  const [activeTab, setActiveTab] = useState<ContactTab>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [isFormDialogOpen, setIsFormDialogOpen] = useState(false)
  const [scrollTop, setScrollTop] = useState(0)

  const LOADING_SKELETON_COUNT = 5
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setDebouncedQuery(searchQuery), 300)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [searchQuery])

  const {
    contacts,
    totalItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refresh,
  } = useInfiniteContactsQuery({
    label: activeTab === 'all' ? undefined : activeTab,
    query: debouncedQuery || undefined,
  })

  const { data: availableLabels = [] } = useQuery({
    queryKey: accountQueries.contactLabelsBase,
    queryFn: () => accountApiService.getContactLabels(),
    enabled: isAuthenticated,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (activeTab !== 'all' && !availableLabels.includes(activeTab as ContactLabel)) {
      setActiveTab('all')
    }
  }, [availableLabels, activeTab])

  const { openGoogleSync, isPending: isGoogleSyncing } = useSyncGoogleContacts({
    onSuccess: (result) => {
      refresh()
      toast.success(t('contacts.googleContactsSync.syncComplete', {
        imported: result.imported,
        updated: result.updated,
        skipped: result.skipped,
        failed: result.failed,
      }))
    },
    onError: (error) => {
      toast.error(error.message ?? t('contacts.googleContactsSync.syncError'))
    },
  })

  useEffect(() => { if (error) toast.error(error.message) }, [error])

  const { state: pullState, pullDistance, isRefreshing } = usePullToRefresh(scrollContainerRef, {
    onRefresh: refresh,
    disabled: isDesktop,
  })

  // Build rows in API order (append-only) + skeleton rows while fetching
  const rows = useMemo(() => {
    const result = buildRows(contacts)
    if (isFetchingNextPage) {
      for (let i = 0; i < LOADING_SKELETON_COUNT; i++) {
        result.push({ type: 'skeleton', id: i })
      }
    }
    return result
  }, [contacts, isFetchingNextPage])

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: (index) => rows[index]?.type === 'header' ? HEADER_HEIGHT : ROW_HEIGHT,
    overscan: 8,
  })

  // Fetch next page when near the end of loaded rows
  const virtualItems = rowVirtualizer.getVirtualItems()
  const lastVirtualItemIndex = virtualItems.length > 0 ? virtualItems[virtualItems.length - 1].index : -1

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return
    const realRowCount = rows.length - (isFetchingNextPage ? LOADING_SKELETON_COUNT : 0)
    if (lastVirtualItemIndex >= realRowCount - 4) fetchNextPage()
  }, [lastVirtualItemIndex, rows.length, hasNextPage, isFetchingNextPage, fetchNextPage])

  // Scroll tracking for sticky section headers
  useEffect(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const onScroll = () => setScrollTop(el.scrollTop)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  const headerPositions = useMemo(() => {
    const result: { letter: string; offset: number }[] = []
    let offset = 0
    for (const row of rows) {
      if (row.type === 'header') result.push({ letter: row.letter, offset })
      offset += row.type === 'header' ? HEADER_HEIGHT : ROW_HEIGHT
    }
    return result
  }, [rows])

  const { stickyLetter, stickyTranslateY } = useMemo(() => {
    if (headerPositions.length === 0) return { stickyLetter: null, stickyTranslateY: 0 }
    const current = [...headerPositions].reverse().find(h => h.offset <= scrollTop)
    if (!current) return { stickyLetter: null, stickyTranslateY: 0 }
    const next = headerPositions.find(h => h.offset > scrollTop)
    const distToNext = next ? next.offset - scrollTop : Infinity
    const translateY = distToNext < HEADER_HEIGHT ? distToNext - HEADER_HEIGHT : 0
    return { stickyLetter: current.letter, stickyTranslateY: translateY }
  }, [headerPositions, scrollTop])

  const handleFormSuccess = () => {
    setIsFormDialogOpen(false)
    refresh()
    queryClient.invalidateQueries({ queryKey: accountQueries.contactLabelsBase })
  }

  const renderContactRow = (contact: Contact) => {
    const displayName = [contact.firstName, contact.lastName].filter(Boolean).join(' ') || t('contacts.unnamed')
    const primaryNumber = contact.phoneNumbers?.[0]
    const extraCount = (contact.phoneNumbers?.length ?? 0) - 1

    return (
      <button
        onClick={() => onOpenContact?.({
          contact,
          phoneNumber: primaryNumber?.number ?? '',
        })}
        className="w-full px-3 flex items-center gap-3 hover:bg-muted/50 transition-colors h-full text-left"
      >
        {/* Avatar */}
        <AvatarPlaceholder seed={displayName} size={36} className="flex-shrink-0" />

        {/* Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-medium text-sm text-foreground truncate leading-tight">{displayName}</span>
            {contact.labels?.includes('favourite') && (
              <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />
            )}
          </div>
          {primaryNumber ? (
            <div className="flex items-center gap-1.5 mt-0.5">
              <PhoneNumber number={primaryNumber.number} className="text-xs text-muted-foreground truncate" />
              {extraCount > 0 && (
                <Badge variant="secondary" className="text-xs py-0 h-4 leading-none shrink-0">+{extraCount}</Badge>
              )}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground italic mt-0.5">{t('contacts.noPhoneNumbers')}</span>
          )}
        </div>

        {/* Call button */}
        {primaryNumber && onCall && (
          <div
            onClick={(e) => { e.stopPropagation(); onCall(primaryNumber.number) }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onCall(primaryNumber.number) } }}
            className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors cursor-pointer"
            title={t('contacts.call')}
          >
            <PhoneCall className="w-3.5 h-3.5 text-primary" />
          </div>
        )}

        {/* Chevron */}
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </button>
    )
  }

  return (
    <AuthOverlay isAuthenticated={isAuthenticated} className={cn('h-full flex flex-col', className)} onLoginSuccess={() => refresh()}>
      {/* Header */}
      <div className="p-6 border-b border-border bg-background sticky top-0 z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold text-foreground">{t('contacts.savedContacts')}</h2>
            {totalItems > 0 && <Badge variant="secondary">{totalItems}</Badge>}
          </div>
          <div className="flex gap-2">
            {isDesktop && (
              <Button variant="ghost" size="sm" onClick={refresh} disabled={isFetchingNextPage || isLoading} title={t('common.refresh', 'Refresh')}>
                <RefreshCw className={cn('w-4 h-4', (isFetchingNextPage || isRefreshing) && 'animate-spin')} />
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isGoogleSyncing} className="group/import">
                  <Import className={cn('w-4 h-4 transition-transform duration-200 group-hover/import:translate-y-0.5', isGoogleSyncing && 'animate-pulse')} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openGoogleSync()}>
                  {t('contacts.importGoogle', 'Google Contacts')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => setIsFormDialogOpen(true)} size="sm" variant="default" className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              {t('contacts.addNew')}
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input type="search" placeholder={t('common.search')} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {(['all', ...availableLabels] as ContactTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={cn(
                'flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                activeTab === tab
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-transparent text-muted-foreground border-border hover:border-foreground hover:text-foreground',
              )}
            >
              {tab === 'favourite' && (
                <Star className={cn('w-3 h-3', activeTab === 'favourite' ? 'fill-current' : 'fill-amber-400 text-amber-400')} />
              )}
              {tab !== 'favourite' && tab}
            </button>
          ))}
        </div>
      </div>

      {/* Scroll container */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-scroll min-h-0">
        {!isDesktop && (
          <PullToRefreshIndicator state={pullState} pullDistance={pullDistance} />
        )}

        {isLoading ? (
          <div className="divide-y divide-border">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="px-3 flex items-center gap-3" style={{ height: ROW_HEIGHT }}>
                <Skeleton className="w-9 h-9 rounded-full shrink-0" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-3.5 w-28" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <Button variant="outline" size="sm" onClick={refresh}>{t('common.retry')}</Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Phone className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {debouncedQuery ? t('common.noResults') : t('contacts.noContacts')}
              </p>
            </div>
          </div>
        ) : (
          <>
            {stickyLetter && (
              <div
                className="sticky top-0 z-10 px-4 bg-background/95 backdrop-blur-sm border-b border-border pointer-events-none"
                style={{ height: HEADER_HEIGHT, marginBottom: -HEADER_HEIGHT, transform: `translateY(${stickyTranslateY}px)` }}
              >
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider leading-none flex items-center h-full">
                  {stickyLetter}
                </span>
              </div>
            )}

            <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
              {virtualItems.map((virtualRow) => {
                const row = rows[virtualRow.index]
                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: 'absolute', top: 0, left: 0, width: '100%',
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    {row.type === 'header' ? (
                      <div className="px-4 bg-muted/40 border-b border-border flex items-center" style={{ height: HEADER_HEIGHT }}>
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{row.letter}</span>
                      </div>
                    ) : row.type === 'skeleton' ? (
                      <div className="px-3 flex items-center gap-3 border-b border-border" style={{ height: ROW_HEIGHT }}>
                        <Skeleton className="w-9 h-9 rounded-full shrink-0" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-3.5 w-28" />
                          <Skeleton className="h-3 w-20" />
                        </div>
                      </div>
                    ) : (
                      <div className="border-b border-border" style={{ height: ROW_HEIGHT }}>
                        {renderContactRow(row.contact)}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Create Contact Dialog */}
      <Dialog open={isFormDialogOpen} onOpenChange={setIsFormDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>{t('contacts.newContact')}</DialogTitle>
          </DialogHeader>
          <ContactForm onSuccess={handleFormSuccess} />
        </DialogContent>
      </Dialog>
    </AuthOverlay>
  )
}

export default ContactsPanel
